import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Search, Trash2, ChevronRight, FileText, CalendarDays, Users, Wallet, RotateCcw, Copy, Send, Link2, Inbox, Handshake, Sparkles, Tag, Star, BarChart3, Boxes, Zap } from "lucide-react";
import { buildCrossSellMessage, buildBookingConfirmationMessage } from "@/lib/whatsappTemplates";
import { CrossSellWhatsAppButton } from "@/components/CrossSellWhatsAppButton";
import { SendBookingWhatsAppButton } from "@/components/SendBookingWhatsAppButton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

const STATUS_OPTIONS = ["new", "contacted", "confirmed", "in_progress", "completed", "cancelled"];

const CROSS_SELL_LABELS = {
  boat_charter: "Boat / Charter",
  scooter_quad: "Scooter / Quad",
  rent_a_car:   "Rent-a-Car",
  guided_tours: "Guided Tours",
};
const STATUS_COLOR = {
  new: "bg-[#C5A059]/15 text-[#C5A059] border-[#C5A059]/30",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  in_progress: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  completed: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings");
      setBookings(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("juke_admin_token");
        navigate("/admin");
        return;
      }
      toast.error("Could not load bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("juke_admin_token")) {
      navigate("/admin");
      return;
    }
    load();
    // eslint-disable-next-line
  }, []);

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      if (filterStatus !== "all" && b.status !== filterStatus) return false;
      if (!query) return true;
      const q = query.toLowerCase();
      return (
        b.name?.toLowerCase().includes(q) ||
        b.email?.toLowerCase().includes(q) ||
        b.phone?.toLowerCase().includes(q) ||
        b.pickup_location?.toLowerCase().includes(q) ||
        b.dropoff_location?.toLowerCase().includes(q)
      );
    });
  }, [bookings, query, filterStatus]);

  const counts = useMemo(() => {
    const c = { total: bookings.length, new: 0, confirmed: 0, completed: 0 };
    bookings.forEach((b) => {
      if (b.status in c) c[b.status]++;
    });
    return c;
  }, [bookings]);

  // Dialog state for CONTACTED transition — admin picks 20% Deposit vs 100% Full
  const [contactedDialog, setContactedDialog] = useState(null);
  // { booking, quoteAmount, mode }

  // Editable premium pricing config (admin-only)
  const [pricingCfg, setPricingCfg] = useState(null);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [addOns, setAddOns] = useState([]);
  useEffect(() => {
    api.get("/admin/pricing-config")
      .then(({ data }) => setPricingCfg(data))
      .catch(() => {});
    api.get("/admin/add-ons")
      .then(({ data }) => setAddOns(data || []))
      .catch(() => {});
  }, []);
  const savePricing = async (patch) => {
    setPricingSaving(true);
    try {
      const { data } = await api.patch("/admin/pricing-config", patch);
      setPricingCfg((c) => ({ ...c, ...data }));
      toast.success("Pricing updated · applies to the next quote");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update pricing");
    } finally {
      setPricingSaving(false);
    }
  };

  const setInterestedStatus = async (booking, code, status) => {
    try {
      await api.patch(`/admin/bookings/${booking.id}/interested-status`, { code, status });
      // Optimistically update the local state so the widget + modal reflect it
      setBookings((list) =>
        list.map((b) =>
          b.id === booking.id
            ? {
                ...b,
                interested_services_status: {
                  ...(b.interested_services_status || {}),
                  [code]: status,
                },
              }
            : b
        )
      );
      setSelected((s) =>
        s && s.id === booking.id
          ? {
              ...s,
              interested_services_status: {
                ...(s.interested_services_status || {}),
                [code]: status,
              },
            }
          : s
      );
      toast.success(`${CROSS_SELL_LABELS[code] || code} → ${status}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update status");
    }
  };

  const applyContactedTransition = async ({ id, quote_amount, payment_mode }) => {
    try {
      const body = { status: "contacted", payment_mode };
      if (Number(quote_amount) > 0) body.quote_amount = Number(quote_amount);
      const { data } = await api.patch(`/admin/bookings/${id}`, body);
      const patch = { status: "contacted", quote_amount: body.quote_amount ?? undefined,
                      stripe_checkout_url: data?.payment?.url,
                      stripe_session_id: data?.payment?.session_id,
                      payment_mode,
                      contacted_at: data?.contacted_at };
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, ...patch } : b)));
      if (selected?.id === id) setSelected((s) => ({ ...s, ...patch }));
      setContactedDialog(null);
      const kindLabel = payment_mode === "full" ? "full payment" : "20% deposit";
      toast.success(
        `Payment link sent · €${Number(data.payment.amount_eur).toFixed(2)} (${kindLabel})`,
        {
          action: {
            label: "Copy link",
            onClick: () => {
              navigator.clipboard.writeText(data.payment.url);
              toast.success("Link copied");
            },
          },
          duration: 8000,
        }
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const updateStatus = async (id, newStatus) => {
    // Transition to CONTACTED opens a dialog with 20% / 100% shortcut buttons.
    if (newStatus === "contacted") {
      const b = bookings.find((x) => x.id === id);
      const bookedTotal = Number(b?.quote_amount) || Number(b?.quote_snapshot?.total_price_eur) || 0;
      setContactedDialog({ booking: b, quoteAmount: bookedTotal });
      return;
    }
    try {
      await api.patch(`/admin/bookings/${id}`, { status: newStatus });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status: newStatus } : b)));
      if (selected?.id === id) setSelected({ ...selected, status: newStatus });
      if (newStatus === "confirmed") {
        toast.success("Confirmed. Booking-confirmed email sent to customer.");
      } else {
        toast.success(`Marked as ${newStatus}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      setBookings((bs) => bs.filter((b) => b.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const resendPaymentLink = async (b, mode) => {
    const chosenMode = mode || b.payment_mode || "deposit";
    const kindLabel = chosenMode === "full" ? "full payment" : "20% deposit";
    if (!window.confirm(
      `Generate a fresh Stripe ${kindLabel} link for ${b.name} and re-email it to ${b.email}?\n\n` +
      `The previous link will still be valid until Stripe expires it (~24h).`
    )) return;
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/resend-payment-link`,
                                       { payment_mode: chosenMode });
      const merged = { ...b, ...(data || {}) };
      setBookings((bs) => bs.map((x) => (x.id === b.id ? merged : x)));
      setSelected(merged);
      toast.success(
        `Fresh ${kindLabel} link sent · €${Number(data?.payment?.amount_eur || 0).toFixed(2)}`,
        {
          action: {
            label: "Copy link",
            onClick: () => {
              navigator.clipboard.writeText(data.payment.url);
              toast.success("Link copied");
            },
          },
          duration: 8000,
        }
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate a new link.");
    }
  };

  const copyPaymentLink = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    toast.success("Payment link copied");
  };

  const refundDeposit = async (b) => {
    const amount = Number(b.deposit_paid || 0);
    if (!amount) return toast.error("No deposit was collected for this booking.");
    if (!window.confirm(
      `Refund €${amount.toFixed(2)} back to the customer via Stripe?\n\nThe booking will be marked as CANCELLED and the customer will receive an automatic email.`
    )) return;
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/refund-deposit`);
      const merged = { ...b, ...(data.booking || {}) };
      setBookings((bs) => bs.map((x) => (x.id === b.id ? merged : x)));
      setSelected(merged);
      toast.success(`Refunded €${(data.amount_eur || amount).toFixed(2)}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refund failed. See backend logs.");
    }
  };

  const logout = () => {
    localStorage.removeItem("juke_admin_token");
    navigate("/admin");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] text-zinc-100">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display text-xl text-white">Juke</span>
            <span className="font-display text-xl text-[#C5A059] italic">Transfers</span>
            <span className="ml-3 text-xs uppercase tracking-[0.2em] text-zinc-500 hidden sm:inline">Control Room</span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/calendar"
              data-testid="nav-calendar"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] text-sm transition-colors"
            >
              <CalendarDays className="w-4 h-4" /> Calendar
            </Link>
            <Link
              to="/admin/drivers-manage"
              data-testid="nav-drivers"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] text-sm transition-colors"
            >
              <Users className="w-4 h-4" /> Drivers
            </Link>
            <Link
              to="/admin/payouts"
              data-testid="nav-payouts"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] text-sm transition-colors"
            >
              <Wallet className="w-4 h-4" /> Payouts
            </Link>
            <Link
              to="/admin/inquiries"
              data-testid="nav-inquiries"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] text-sm transition-colors"
            >
              <Inbox className="w-4 h-4" /> Inquiries
            </Link>
            <Link
              to="/admin/suppliers"
              data-testid="nav-suppliers"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] text-sm transition-colors"
            >
              <Handshake className="w-4 h-4" /> Suppliers
            </Link>
            <Link
              to="/admin/driver-applications"
              data-testid="nav-driver-applications"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] text-sm transition-colors"
            >
              <Users className="w-4 h-4" /> Onboarding
            </Link>
            <Link
              to="/admin/pricing"
              data-testid="nav-pricing"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] text-sm transition-colors"
            >
              <Tag className="w-4 h-4" /> Pricing
            </Link>
            <Link
              to="/admin/analytics"
              data-testid="nav-analytics"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] text-sm transition-colors"
            >
              <BarChart3 className="w-4 h-4" /> Analytics
            </Link>
            <Link
              to="/admin/control-room"
              data-testid="nav-control-room"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 text-sm transition-colors"
            >
              <Boxes className="w-4 h-4" /> Control Room
            </Link>
            <Link
              to="/admin/dispatch"
              data-testid="nav-dispatch"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-sm transition-colors"
            >
              <Zap className="w-4 h-4" /> Dispatch
            </Link>
            <Link
              to="/admin/quotes"
              data-testid="nav-quotes"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 text-sm transition-colors mr-1"
            >
              <FileText className="w-4 h-4" /> Quotes
            </Link>
            <button
              data-testid="refresh-btn"
              onClick={load}
              className="p-2 rounded-full hover:bg-white/5 text-zinc-400 hover:text-[#C5A059] transition-colors"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] text-sm transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: "Total", value: counts.total },
            { label: "New", value: counts.new },
            { label: "Confirmed", value: counts.confirmed },
            { label: "Completed", value: counts.completed },
          ].map((s) => (
            <div key={s.label} className="bg-[#0d0d0d] border border-white/10 rounded-xl p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">{s.label}</p>
              <p className="font-display text-4xl text-white font-light mt-2">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Ratings widget */}
        <RatingsWidget />

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              data-testid="search-input"
              placeholder="Search by name, email, phone, location…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 bg-[#0d0d0d] border-white/10 text-zinc-100 h-11 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/30"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {["all", ...STATUS_OPTIONS].map((s) => (
              <button
                key={s}
                data-testid={`filter-${s}`}
                onClick={() => setFilterStatus(s)}
                className={`px-4 h-11 rounded-full text-xs uppercase tracking-[0.15em] border transition-colors whitespace-nowrap ${
                  filterStatus === s
                    ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5"
                    : "border-white/10 text-zinc-400 hover:text-zinc-100"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Bookings table */}
        <div className="rounded-xl border border-white/10 bg-[#0d0d0d] overflow-hidden">
          <div className="hidden lg:grid grid-cols-12 px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10">
            <div className="col-span-3">Guest</div>
            <div className="col-span-3">Route</div>
            <div className="col-span-2">Date / Time</div>
            <div className="col-span-1 text-center">Pax</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-right">Action</div>
          </div>

          {loading ? (
            <div className="px-6 py-12 text-center text-zinc-500" data-testid="loading-state">Loading bookings…</div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-16 text-center" data-testid="empty-state">
              <p className="text-zinc-400">No bookings match your filters yet.</p>
              <p className="text-zinc-600 text-sm mt-2">Inquiries from the website will appear here in real time.</p>
            </div>
          ) : (
            filtered.map((b) => (
              <div
                key={b.id}
                data-testid={`booking-row-${b.id}`}
                onClick={() => setSelected(b)}
                className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-0 px-6 py-5 border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors"
              >
                <div className="lg:col-span-3">
                  <p className="text-white font-medium">{b.name}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{b.email}</p>
                </div>
                <div className="lg:col-span-3 text-sm text-zinc-300">
                  <p>{b.pickup_location}</p>
                  <p className="text-zinc-500 text-xs mt-0.5">→ {b.dropoff_location}</p>
                </div>
                <div className="lg:col-span-2 text-sm text-zinc-300">
                  <p>{b.pickup_date}</p>
                  <p className="text-zinc-500 text-xs">{b.pickup_time}</p>
                </div>
                <div className="lg:col-span-1 lg:text-center text-zinc-300 text-sm">{b.passengers}</div>
                <div className="lg:col-span-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`status-trigger-${b.id}`}>
                        <Badge variant="outline" className={`${STATUS_COLOR[b.status]} uppercase tracking-wider text-[10px] px-3 py-1 cursor-pointer`}>
                          {b.status}
                        </Badge>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#0d0d0d] border-white/10 text-zinc-100">
                      {STATUS_OPTIONS.map((s) => (
                        <DropdownMenuItem
                          key={s}
                          onClick={() => updateStatus(b.id, s)}
                          className="cursor-pointer focus:bg-white/5 focus:text-[#C5A059] capitalize"
                        >
                          {s}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {b.stripe_checkout_url && !(b.deposit_paid > 0) && (
                    <span
                      data-testid={`payment-pending-badge-${b.id}`}
                      title={
                        b.payment_reminder_sent_at
                          ? `Auto-reminder sent ${new Date(b.payment_reminder_sent_at).toLocaleString()}`
                          : b.payment_link_resent_at
                            ? `Link resent ${new Date(b.payment_link_resent_at).toLocaleString()}`
                            : `Payment link sent ${b.contacted_at ? new Date(b.contacted_at).toLocaleString() : ""}`
                      }
                      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                        b.payment_reminder_sent_at
                          ? "border-amber-400/40 text-amber-300 bg-amber-400/5"
                          : "border-blue-400/40 text-blue-300 bg-blue-400/5"
                      }`}
                    >
                      <Link2 className="w-3 h-3" />
                      {b.payment_reminder_sent_at ? "Reminded" : "Awaiting €"}
                    </span>
                  )}
                </div>
                <div className="lg:col-span-1 flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    data-testid={`delete-${b.id}`}
                    onClick={() => remove(b.id)}
                    className="p-2 text-zinc-500 hover:text-red-400 transition-colors"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <ChevronRight className="w-4 h-4 text-zinc-600 hidden lg:block" />
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="booking-detail-dialog" className="bg-[#0d0d0d] border-white/10 text-zinc-100 max-w-2xl max-h-[92vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-light text-white">{selected.name}</DialogTitle>
                <DialogDescription className="text-zinc-500">
                  Submitted {fmtDateTime(selected.created_at)}
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                <Detail label="Email" value={selected.email} />
                <Detail label="Phone" value={selected.phone} />
                <div className="col-span-2">
                  <Detail
                    label="Booking type"
                    value={
                      selected.booking_type === "hourly" ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-purple-500/10 border border-purple-500/40 text-purple-200 text-[11px] uppercase tracking-widest">
                          🕒 Hourly Charter · {selected.duration_hours || selected.quote_snapshot?.duration_hours || 0} h
                        </span>
                      ) : (
                        "Point-to-point transfer"
                      )
                    }
                  />
                </div>
                <Detail label="Pickup" value={selected.pickup_location} />
                <Detail label="Drop-off" value={selected.booking_type === "hourly" ? "—" : (selected.dropoff_location || "—")} />
                <Detail label="Date" value={selected.pickup_date} />
                <Detail label="Time" value={selected.pickup_time} />
                <Detail label="Passengers" value={selected.passengers} />
                <Detail label="Luggage" value={selected.luggage ?? 0} />
                <Detail label="Vehicle" value={selected.vehicle_type} />
                <Detail label="Reference" value={selected.ref || "—"} />
                {selected.flight_number && <Detail label="Flight" value={selected.flight_number} />}
                {selected.quote_snapshot?.distance_km > 0 && (
                  <Detail
                    label="Route"
                    value={`${selected.quote_snapshot.distance_km} km · ${selected.quote_snapshot.duration_min} min`}
                  />
                )}
                {Array.isArray(selected.interested_services) && selected.interested_services.length > 0 && (
                  <div className="col-span-2">
                    <Detail
                      label="Also interested in"
                      value={
                        <div
                          data-testid="cross-sell-detail"
                          className="flex flex-col gap-2 pt-1"
                        >
                          {selected.interested_services.map((code) => {
                            const status = (selected.interested_services_status || {})[code] || "pending";
                            const waMessage = buildCrossSellMessage(selected, code);
                            const serviceLabel = CROSS_SELL_LABELS[code] || code;
                            return (
                              <div
                                key={code}
                                data-testid={`cross-sell-row-${code}`}
                                className="flex items-center justify-between gap-3 flex-wrap"
                              >
                                <span
                                  data-testid={`cross-sell-badge-${code}`}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#C5A059]/10 border border-[#C5A059]/40 text-[#C5A059] text-[11px] uppercase tracking-widest"
                                >
                                  <Sparkles className="w-3 h-3" />
                                  {serviceLabel}
                                </span>
                                <div className="flex items-center gap-2">
                                  <CrossSellWhatsAppButton
                                    phone={selected.phone}
                                    defaultMessage={waMessage}
                                    serviceLabel={serviceLabel}
                                    customerName={selected.name}
                                    testIdSuffix={code}
                                  />
                                  <select
                                    data-testid={`cross-sell-status-${code}`}
                                    value={status}
                                    onChange={(e) => setInterestedStatus(selected, code, e.target.value)}
                                    className={`h-7 text-[10px] uppercase tracking-widest bg-zinc-950 border rounded-full px-3 focus:outline-none ${
                                      status === "booked"
                                        ? "border-emerald-500/40 text-emerald-300"
                                        : status === "declined"
                                          ? "border-red-500/30 text-red-300"
                                          : "border-white/10 text-zinc-400"
                                    }`}
                                  >
                                    <option value="pending">Pending</option>
                                    <option value="booked">Booked</option>
                                    <option value="declined">Declined</option>
                                  </select>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      }
                    />
                  </div>
                )}
                {selected.is_round_trip && (
                  <div className="col-span-2">
                    <Detail
                      label="Round trip"
                      value={
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/40 text-blue-200 text-[11px] uppercase tracking-widest">
                          ↔ Return {selected.return_date || "—"} at {selected.return_time || "—"}
                          {selected.waiting_hours > 0 && ` · ${selected.waiting_hours}h wait`}
                        </span>
                      }
                    />
                  </div>
                )}
                <Detail
                  label="Deposit"
                  value={
                    selected.deposit_refunded > 0
                      ? `€${Number(selected.deposit_refunded).toFixed(2)} refunded`
                      : selected.deposit_paid > 0
                        ? `€${Number(selected.deposit_paid).toFixed(2)} paid`
                        : "—"
                  }
                />
                <div className="col-span-2">
                  <Detail label="Notes" value={selected.notes || "—"} />
                </div>
              </div>

              {/* Price breakdown — the exact numbers the customer saw */}
              {selected.quote_snapshot && (
                <div
                  data-testid={`price-breakdown-${selected.id}`}
                  className="mt-6 pt-6 border-t border-white/10 rounded-xl bg-[#C5A059]/[0.04] border border-[#C5A059]/20 p-5"
                >
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#C5A059] mb-3 flex items-center gap-2">
                    <Wallet className="w-3.5 h-3.5" /> Price breakdown
                  </p>
                  <div className="space-y-1.5 text-sm">
                    {selected.booking_type === "hourly" ? (
                      <>
                        <Row label={`Hourly rate × ${selected.quote_snapshot.duration_hours || 0} h`}
                             value={`€${Number(selected.quote_snapshot.base_price_eur || 0).toFixed(2)}`} />
                      </>
                    ) : (
                      <>
                        {selected.quote_snapshot.is_fixed_route ? (
                          <Row label={`Fixed route (${selected.quote_snapshot.fixed_route_label || "—"})`}
                               value={`€${Number(selected.quote_snapshot.fixed_route_base_eur || selected.quote_snapshot.base_price_eur || 0).toFixed(2)}`} />
                        ) : (
                          <Row label="Base fare (start + km)"
                               value={`€${Number(selected.quote_snapshot.base_price_eur || 0).toFixed(2)}`} />
                        )}
                        {selected.quote_snapshot.return_leg_eur > 0 && (
                          <Row label="Return leg"
                               value={`€${Number(selected.quote_snapshot.return_leg_eur).toFixed(2)}`} />
                        )}
                        {selected.quote_snapshot.waiting_fee_eur > 0 && (
                          <Row label={`Waiting (${selected.quote_snapshot.billed_waiting_hours || "—"} h)`}
                               value={`€${Number(selected.quote_snapshot.waiting_fee_eur).toFixed(2)}`} />
                        )}
                        {selected.quote_snapshot.luggage_fee_eur > 0 && (
                          <Row label={`Luggage × ${selected.luggage || 0}`}
                               value={`€${Number(selected.quote_snapshot.luggage_fee_eur).toFixed(2)}`} />
                        )}
                        {selected.quote_snapshot.night_surcharge && (
                          <Row label="Night rate applied" value="✓" mute />
                        )}
                      </>
                    )}
                    {selected.upsells?.length > 0 && (
                      <div className="pt-2 mt-2 border-t border-white/5">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Add-ons selected</p>
                        {selected.upsells.map((a, i) => {
                          const code = a.code || a.name;
                          const qty = Number(a.quantity || a.qty || 1);
                          // Resolve unit price + label from the live catalog first
                          // (booking rows store only {code, qty}); fall back to any
                          // legacy embedded price fields for old records.
                          const catalog = addOns.find((x) => x.code === code) || {};
                          const unitPrice = Number(
                            catalog.price ?? a.price_eur ?? a.price ?? a.unit_price_eur ?? 0
                          );
                          const label = catalog.label || a.name || code;
                          return (
                            <Row
                              key={i}
                              label={`  ↳ ${qty}× ${label}`}
                              value={`€${(unitPrice * qty).toFixed(2)}`}
                              mute
                            />
                          );
                        })}
                      </div>
                    )}
                    {selected.quote_snapshot.upsells_total_eur > 0 && (
                      <Row label="Add-ons total"
                           value={`€${Number(selected.quote_snapshot.upsells_total_eur).toFixed(2)}`} />
                    )}
                    <div className="pt-2 mt-2 border-t border-white/10 flex justify-between text-zinc-400">
                      <span>Subtotal</span>
                      <span className="tabular-nums">
                        €{Number(selected.quote_snapshot.subtotal_eur ??
                          (Number(selected.quote_snapshot.total_price_eur || 0) + Number(selected.quote_snapshot.discount_eur || 0))
                        ).toFixed(2)}
                      </span>
                    </div>
                    {selected.quote_snapshot.discount_eur > 0 && (
                      <div className="flex justify-between text-emerald-400">
                        <span>
                          Promo {selected.quote_snapshot.promo_code} (−{selected.quote_snapshot.discount_percent}%)
                        </span>
                        <span className="tabular-nums">
                          −€{Number(selected.quote_snapshot.discount_eur).toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="pt-2 mt-2 border-t border-[#C5A059]/40 flex justify-between text-base">
                      <span className="text-[#C5A059] uppercase tracking-widest text-xs font-semibold">Total price</span>
                      <span className="text-[#C5A059] font-bold tabular-nums">
                        €{Number(selected.quote_snapshot.total_price_eur || selected.quote_amount || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* One-click "Send booking to WhatsApp" — composed confirmation with ref, pickup, driver, Maps link */}
              <div
                data-testid={`booking-whatsapp-card-${selected.id}`}
                className="mt-6 pt-6 border-t border-white/10 rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-4 flex items-center gap-3 flex-wrap"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium">Booking confirmation via WhatsApp</p>
                  <p className="text-xs text-zinc-400">
                    Opens a pre-composed message (ref, pickup time, driver, Google Maps pin) — you tap Send in WhatsApp.
                    {selected.last_whatsapp_sent_at && (
                      <> · Last sent {new Date(selected.last_whatsapp_sent_at).toLocaleString()}</>
                    )}
                  </p>
                </div>
                <SendBookingWhatsAppButton
                  booking={selected}
                  defaultMessage={buildBookingConfirmationMessage(selected)}
                />
              </div>

              {/* Payment link — visible when a Stripe deposit session exists AND deposit not yet paid */}
              {selected.stripe_checkout_url && !(selected.deposit_paid > 0) && (
                <div
                  data-testid={`payment-link-card-${selected.id}`}
                  className="mt-6 pt-6 border-t border-white/10 rounded-xl bg-blue-500/5 border border-blue-500/20 p-4"
                >
                  <div className="flex items-start gap-3">
                    <Link2 className="w-5 h-5 text-blue-300 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium">
                        {selected.payment_mode === "full" ? "Full-payment link" : "Deposit payment link"}
                        <span className="ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/10 text-zinc-400">
                          {selected.payment_mode === "full" ? "100% Full" : "20% Deposit"}
                        </span>
                      </p>
                      <p className="text-[11px] text-zinc-400 mt-0.5">
                        Sent to <span className="text-zinc-300">{selected.email}</span>
                        {selected.contacted_at && ` · ${new Date(selected.contacted_at).toLocaleString()}`}
                      </p>
                      {selected.payment_reminder_sent_at && (
                        <p className="mt-1 text-[11px] text-amber-300">
                          ⏰ Auto-reminder sent {new Date(selected.payment_reminder_sent_at).toLocaleString()}
                        </p>
                      )}
                      {selected.payment_link_resent_at && (
                        <p className="mt-1 text-[11px] text-blue-300">
                          🔁 Manually resent {new Date(selected.payment_link_resent_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      data-testid={`copy-payment-link-btn-${selected.id}`}
                      onClick={() => copyPaymentLink(selected.stripe_checkout_url)}
                      className="h-9 px-4 rounded-full border border-white/15 hover:border-blue-400 text-blue-200 text-[11px] uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors"
                    >
                      <Copy className="w-3.5 h-3.5" /> Copy link
                    </button>
                    <a
                      href={selected.stripe_checkout_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`open-payment-link-${selected.id}`}
                      className="h-9 px-4 rounded-full border border-white/15 hover:border-blue-400 text-blue-200 text-[11px] uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors"
                    >
                      <Link2 className="w-3.5 h-3.5" /> Open Stripe
                    </a>
                    <button
                      type="button"
                      data-testid={`resend-payment-link-btn-${selected.id}`}
                      onClick={() => resendPaymentLink(selected, selected.payment_mode || "deposit")}
                      className="h-9 px-4 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-[11px] uppercase tracking-[0.15em] font-semibold flex items-center gap-1.5 transition-colors"
                    >
                      <Send className="w-3.5 h-3.5" /> Resend {selected.payment_mode === "full" ? "100% Full" : "20% Deposit"}
                    </button>
                    <button
                      type="button"
                      data-testid={`resend-alt-mode-btn-${selected.id}`}
                      onClick={() => resendPaymentLink(selected, selected.payment_mode === "full" ? "deposit" : "full")}
                      className="h-9 px-4 rounded-full border border-white/15 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] text-[11px] uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors"
                    >
                      Switch to {selected.payment_mode === "full" ? "20% Deposit" : "100% Full"}
                    </button>
                  </div>
                </div>
              )}

              {selected.deposit_paid > 0 && !selected.deposit_refunded && (
                <div className="mt-6 pt-6 border-t border-white/10 rounded-xl bg-[#C5A059]/5 border border-[#C5A059]/20 p-4 flex items-center gap-3">
                  <RotateCcw className="w-5 h-5 text-[#C5A059] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium">Refund deposit</p>
                    <p className="text-xs text-zinc-400">
                      Refunds €{Number(selected.deposit_paid).toFixed(2)} via Stripe and marks the booking as cancelled.
                    </p>
                  </div>
                  <button
                    onClick={() => refundDeposit(selected)}
                    data-testid={`refund-deposit-btn-${selected.id}`}
                    className="h-10 px-4 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-xs font-semibold uppercase tracking-[0.15em] transition-colors"
                  >
                    Refund €{Number(selected.deposit_paid).toFixed(2)}
                  </button>
                </div>
              )}
              {selected.deposit_refunded > 0 && (
                <div
                  data-testid={`refund-badge-${selected.id}`}
                  className="mt-6 pt-6 border-t border-white/10 rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-4 text-sm text-emerald-300 flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  €{Number(selected.deposit_refunded).toFixed(2)} refunded on {selected.deposit_refunded_at?.slice(0, 10) || "—"}.
                </div>
              )}
              <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(selected.id, s)}
                    className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.15em] border transition ${
                      selected.status === s
                        ? STATUS_COLOR[s]
                        : "border-white/10 text-zinc-500 hover:text-zinc-100"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* CONTACTED transition — pick 20% Deposit or 100% Full, then send payment link */}
      <ContactedDialog
        state={contactedDialog}
        onClose={() => setContactedDialog(null)}
        onConfirm={applyContactedTransition}
      />
    </div>
  );
}

function Detail({ label, value }) {
  const isElement = typeof value === "object" && value !== null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
      {isElement ? (
        <div className="text-zinc-100 mt-1 break-words">{value}</div>
      ) : (
        <p className="text-zinc-100 mt-1 break-words">{value}</p>
      )}
    </div>
  );
}


function Row({ label, value, mute = false }) {
  return (
    <div className={`flex justify-between items-baseline ${mute ? "text-zinc-500 text-[13px]" : "text-zinc-300"}`}>
      <span className="whitespace-pre">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function ContactedDialog({ state, onClose, onConfirm }) {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("deposit");

  React.useEffect(() => {
    if (state) {
      const total = Number(state.quoteAmount) || 0;
      setAmount(total > 0 ? total.toFixed(2) : "");
      setMode("deposit");
    }
  }, [state]);

  if (!state) return null;
  const b = state.booking;
  const total = Number(amount) || 0;
  const deposit20 = total > 0 ? total * 0.2 : 0;
  const chargeNow = mode === "full" ? total : deposit20;

  const snap = (nextMode) => {
    setMode(nextMode);
    // no numeric change to the input — mode alone drives what Stripe charges
  };

  const canSend = total > 0;

  const submit = async () => {
    if (!canSend) return;
    await onConfirm({ id: b.id, quote_amount: total, payment_mode: mode });
  };

  return (
    <Dialog open={!!state} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        data-testid="contacted-dialog"
        className="bg-[#0d0d0d] border-white/10 text-zinc-100 max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="text-[#C5A059] font-light text-2xl">Send payment link</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {b?.name} · {b?.pickup_location}
            {b?.dropoff_location && b?.booking_type !== "hourly" && ` → ${b.dropoff_location}`}
            {b?.booking_type === "hourly" && ` · Hourly ${b?.duration_hours || 0}h`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Confirmed quote total (€)
            </label>
            <Input
              type="number"
              min="0"
              step="0.01"
              data-testid="contacted-amount-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 bg-zinc-900 border-white/10 text-lg h-11 tabular-nums"
              placeholder="e.g. 60.00"
            />
            <p className="mt-1 text-[11px] text-zinc-500">
              Total the customer will owe. The Stripe charge below is derived from it.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="snap-deposit-btn"
              onClick={() => snap("deposit")}
              className={`p-4 rounded-xl border transition-all text-left ${
                mode === "deposit"
                  ? "border-[#C5A059] bg-[#C5A059]/10 text-[#C5A059]"
                  : "border-white/10 hover:border-white/30 text-zinc-300"
              }`}
            >
              <div className="text-[10px] uppercase tracking-widest opacity-70">Snap</div>
              <div className="text-lg font-semibold mt-1">20% Deposit</div>
              <div className="text-2xl font-bold mt-1 tabular-nums">
                €{deposit20.toFixed(2)}
              </div>
              <div className="text-[11px] opacity-70 mt-1">
                Rest paid to driver on the day
              </div>
            </button>
            <button
              type="button"
              data-testid="snap-full-btn"
              onClick={() => snap("full")}
              className={`p-4 rounded-xl border transition-all text-left ${
                mode === "full"
                  ? "border-[#C5A059] bg-[#C5A059]/10 text-[#C5A059]"
                  : "border-white/10 hover:border-white/30 text-zinc-300"
              }`}
            >
              <div className="text-[10px] uppercase tracking-widest opacity-70">Snap</div>
              <div className="text-lg font-semibold mt-1">100% Full</div>
              <div className="text-2xl font-bold mt-1 tabular-nums">
                €{total.toFixed(2)}
              </div>
              <div className="text-[11px] opacity-70 mt-1">
                Nothing to collect on the day
              </div>
            </button>
          </div>

          <div className="p-3 rounded-lg bg-white/5 border border-white/10 text-sm flex items-center justify-between">
            <span className="text-zinc-400">Stripe will charge now:</span>
            <span className="text-[#C5A059] font-bold text-xl tabular-nums">
              €{chargeNow.toFixed(2)}
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            data-testid="contacted-cancel-btn"
            onClick={onClose}
            className="h-10 px-4 rounded-full border border-white/15 text-zinc-300 text-[11px] uppercase tracking-[0.15em]"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="contacted-send-btn"
            onClick={submit}
            disabled={!canSend}
            className="h-10 px-6 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] disabled:opacity-40 text-black text-[11px] uppercase tracking-[0.15em] font-semibold flex items-center gap-2"
          >
            <Send className="w-3.5 h-3.5" />
            Send link · €{chargeNow.toFixed(2)}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Ratings widget — compact card near the top of the admin dashboard.
 *   • Avg ⭐ + count for the last 7 and 30 days
 *   • Last 5 negative comments (≤ 3★) so the operator can act on quality
 *     issues without needing to open Mongo.
 * Hidden entirely when no ratings exist yet — no visual noise on day 1.
 */
function RatingsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/ratings/summary");
      setData(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return null;
  const hasAny = (data?.thirty_days?.count || 0) > 0
              || (data?.seven_days?.count || 0) > 0;
  if (!data || !hasAny) return null;

  const renderStars = (avg) => {
    const rounded = Math.round((avg || 0) * 10) / 10;
    const full = Math.floor(rounded);
    return (
      <span className="inline-flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            className={`w-4 h-4 ${n <= full ? "text-[#C5A059] fill-[#C5A059]" : "text-zinc-700"}`}
          />
        ))}
        <span className="ml-2 text-white font-display text-2xl">{rounded.toFixed(1)}</span>
      </span>
    );
  };

  const seven = data.seven_days || { avg: null, count: 0 };
  const thirty = data.thirty_days || { avg: null, count: 0 };
  const negs = data.recent_negative || [];

  return (
    <div
      data-testid="ratings-widget"
      className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10"
    >
      <div className="bg-[#0d0d0d] border border-white/10 rounded-xl p-5">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Last 7 days</p>
          <span className="text-[10px] uppercase tracking-widest text-zinc-600">
            {seven.count} rating{seven.count === 1 ? "" : "s"}
          </span>
        </div>
        <div className="mt-3">
          {seven.avg ? renderStars(seven.avg) : <span className="text-zinc-600 text-sm">No ratings yet</span>}
        </div>
      </div>
      <div className="bg-[#0d0d0d] border border-white/10 rounded-xl p-5">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Last 30 days</p>
          <span className="text-[10px] uppercase tracking-widest text-zinc-600">
            {thirty.count} rating{thirty.count === 1 ? "" : "s"}
          </span>
        </div>
        <div className="mt-3">
          {thirty.avg ? renderStars(thirty.avg) : <span className="text-zinc-600 text-sm">No ratings yet</span>}
        </div>
      </div>
      <div
        data-testid="ratings-negative-panel"
        className="bg-[#0d0d0d] border border-red-500/20 rounded-xl p-5 lg:col-span-1"
      >
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-[0.2em] text-red-400/90">
            Recent negative feedback
          </p>
          <span className="text-[10px] uppercase tracking-widest text-zinc-600">
            {negs.length} to review
          </span>
        </div>
        {negs.length === 0 ? (
          <p className="text-zinc-500 text-sm mt-3">
            No negative comments — nice work.
          </p>
        ) : (
          <ul className="mt-3 space-y-3">
            {negs.map((n) => (
              <li key={n.ref + n.submitted_at} data-testid={`neg-${n.ref}`}
                  className="text-xs">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500">
                  <span>{n.ref}</span>
                  <span className="flex items-center gap-0.5 text-red-400">
                    {n.stars}<Star className="w-3 h-3 fill-red-400" />
                  </span>
                </div>
                <p className="text-zinc-300 mt-1 line-clamp-2">{n.comment}</p>
                {n.email && (
                  <a href={`mailto:${n.email}?subject=Regarding your recent Juke Transfers ride ${n.ref}`}
                     className="text-[10px] uppercase tracking-widest text-[#C5A059] hover:text-[#D4AF37]">
                    reply to {n.email}
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

